import { useState } from 'react';
import { DollarSign, Loader2, X, CheckCircle } from 'lucide-react';
import { gerarFaturamento, getPreviewFaturamento, PreviewFaturamento } from '../../services/faturamentoService';
import { Button } from '../../components/ui/button';

interface FaturamentoButtonProps {
  clientId: string;
  onSuccess?: () => void;
}

export const FaturamentoButton = ({ clientId, onSuccess }: FaturamentoButtonProps) => {
  const [showModal, setShowModal] = useState(false);
  const [preview, setPreview] = useState<PreviewFaturamento | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    numeroFatura: number;
    valorTotal: number;
    quantidadeApontamentos: number;
  } | null>(null);

  const handleOpenModal = async () => {
    try {
      setLoading(true);
      setError(null);
      setShowModal(true);
      
      const previewData = await getPreviewFaturamento(clientId);
      setPreview(previewData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar preview');
      console.error('Erro ao carregar preview:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setProcessing(true);
      setError(null);

      const result = await gerarFaturamento({ clientId });
      
      setSuccess({
        numeroFatura: result.numeroFatura,
        valorTotal: result.valorTotal,
        quantidadeApontamentos: result.quantidadeApontamentos,
      });

      // Aguardar 2 segundos antes de fechar e atualizar
      setTimeout(() => {
        setShowModal(false);
        setSuccess(null);
        setPreview(null);
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao gerar faturamento');
      console.error('Erro ao gerar faturamento:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (!processing) {
      setShowModal(false);
      setPreview(null);
      setError(null);
      setSuccess(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    // Extrai apenas YYYY-MM-DD da string ISO, ignora timezone
    const datePart = dateString.substring(0, 10);
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeString: string) => {
    // Extrai apenas HH:mm da string ISO, ignora timezone
    const timePart = timeString.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  return (
    <>
      <Button onClick={handleOpenModal} variant="secondary">
        <DollarSign />
        Faturar em Lote
      </Button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">
                {success ? 'Faturamento Concluído!' : 'Preview de Faturamento'}
              </h3>
              {!processing && (
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-brand" />
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {success && (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-foreground mb-2">
                    Fatura #{success.numeroFatura}
                  </h4>
                  <p className="text-muted-foreground mb-4">
                    {success.quantidadeApontamentos} apontamento(s) faturado(s)
                  </p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(success.valorTotal)}
                  </p>
                </div>
              )}

              {preview && !success && (
                <div className="space-y-6">
                  {/* Resumo */}
                  <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Total de Apontamentos</p>
                        <p className="text-2xl font-bold text-foreground">
                          {preview.quantidadeApontamentos}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Valor Total</p>
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(preview.valorTotal)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Totais por Tipo */}
                  {preview.totaisPorTipo.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">
                        Totais por Tipo de Hora
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {preview.totaisPorTipo.map((tipo) => (
                          <div
                            key={tipo.tipoHora}
                            className="bg-muted/50 border border-border rounded-lg p-3"
                          >
                            <p className="text-sm font-medium text-muted-foreground">
                              {tipo.tipoHora}
                            </p>
                            <p className="text-lg font-bold text-foreground">
                              {tipo.quantidade.toFixed(2)} horas
                            </p>
                            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                              {formatCurrency(tipo.valorTotal)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lista de Apontamentos */}
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">
                      Apontamentos a Faturar
                    </h4>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                Data
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                Ticket
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                Técnico
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                Horário
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                                Tipo
                              </th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                                Valor
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {preview.apontamentos.map((apt) => (
                              <tr key={apt.id} className="hover:bg-muted/50">
                                <td className="px-4 py-3 text-sm text-foreground">
                                  {formatDate(apt.data)}
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground">
                                  <div>
                                    <p className="font-medium">{apt.ticket.codigo}</p>
                                    <p className="text-xs text-muted-foreground truncate max-w-xs">
                                      {apt.ticket.titulo}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground">
                                  {apt.user.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-foreground">
                                  {formatTime(apt.horaInicio)} - {formatTime(apt.horaFim)}
                                  <span className="text-xs text-muted-foreground ml-2">
                                    ({Math.floor(apt.minutosTotais / 60)}h{apt.minutosTotais % 60}m)
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                    {apt.tipoHora}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-right font-semibold text-foreground">
                                  {formatCurrency(apt.valorCalculado)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {preview.quantidadeApontamentos === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum apontamento disponível para faturamento
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            {preview && !success && preview.quantidadeApontamentos > 0 && (
              <div className="flex items-center justify-end gap-4 p-6 border-t border-border bg-muted/50">
                <Button
                  onClick={handleClose}
                  disabled={processing}
                  variant="outline"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={processing}
                >
                  {processing && <Loader2 className="animate-spin" />}
                  Confirmar Faturamento
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
